/**
 * ResidentActionItems — Displays action items from Resident Trends for a
 * specific resident on their profile page.
 *
 * Fetches the campus-wide action items and filters to the current resident.
 * Only renders the action card list (no summary/expandable trigger sections).
 *
 * Props:
 *   campusId   — current campus ID
 *   residentId — the resident to filter for
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuthHeaders } from '../../../../../services/auth';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Configuration (mirrors dashboard ResidentTrendActions)
// ---------------------------------------------------------------------------
const TRIGGER_CONFIG = {
    volume_spike:        { label: 'Volume Spike',        icon: '📈', color: '#e74c3c' },
    sustained_increase:  { label: 'Sustained Increase',  icon: '📊', color: '#e67e22' },
    high_frequency_new:  { label: 'New High-Frequency',  icon: '🔔', color: '#8e44ad' },
    nighttime_increase:  { label: 'Nighttime Increase',  icon: '🌙', color: '#2c3e50' },
    reason_shift:        { label: 'Reason Shift',        icon: '🔄', color: '#2980b9' },
    new_reason:          { label: 'New Reason',          icon: '⚠️', color: '#16a085' },
};

const SEVERITY_COLORS = { high: '#e74c3c', medium: '#e67e22', low: '#16a085' };

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------
const WeeklySparkline = ({ data, color }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data, 1);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 24 }}>
            {data.map((v, i) => (
                <div key={i} style={{ width: 6, height: `${Math.max((v / max) * 100, 4)}%`, background: i === data.length - 1 ? color : `${color}60`, borderRadius: 2 }} />
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Metric Pill
// ---------------------------------------------------------------------------
const MetricPill = ({ label, value }) => (
    <div style={{ background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', padding: '6px 12px', display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</span>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>{value}</span>
    </div>
);

// ---------------------------------------------------------------------------
// Metrics helper
// ---------------------------------------------------------------------------
function getMetrics(item) {
    const sd = typeof item.supporting_data === 'string' ? JSON.parse(item.supporting_data) : item.supporting_data || {};
    switch (item.trigger_type) {
        case 'volume_spike':
            return [
                { label: 'This Week', value: sd.week1 ?? sd.week1_current ?? '—' },
                { label: 'Avg/Week', value: sd.avg_weekly ?? '—' },
                { label: '% Increase', value: sd.pct_increase ? `${sd.pct_increase}%` : '—' },
            ];
        case 'sustained_increase':
            return [
                { label: 'This Week', value: sd.week1 ?? sd.week1_current ?? '—' },
                { label: 'Weeks Rising', value: sd.weeks_rising ?? '—' },
            ];
        case 'high_frequency_new':
            return [
                { label: 'This Week', value: sd.week1 ?? sd.week1_current ?? '—' },
                { label: 'Prior Avg', value: sd.prior_avg ?? '—' },
                { label: 'Facility P75', value: sd.facility_p75 ?? '—' },
            ];
        case 'nighttime_increase':
            return [
                { label: 'Night Calls', value: sd.night_week1 ?? '—' },
                { label: 'Prior Avg', value: sd.night_prior_avg ?? '—' },
                { label: 'Night %', value: sd.night_pct ? `${sd.night_pct}%` : '—' },
            ];
        case 'reason_shift':
            return [
                { label: 'Reason', value: sd.reason_name ?? '—' },
                { label: 'Now', value: sd.current_pct ? `${sd.current_pct}%` : '—' },
                { label: 'Was', value: sd.prior_pct ? `${sd.prior_pct}%` : '—' },
            ];
        case 'new_reason':
            return [
                { label: 'Reason', value: sd.reason_name ?? '—' },
                { label: 'Calls', value: sd.call_count ?? '—' },
                { label: '% Total', value: sd.pct_total ? `${sd.pct_total}%` : '—' },
            ];
        default:
            return [];
    }
}

function getWeeklyData(item) {
    const sd = typeof item.supporting_data === 'string' ? JSON.parse(item.supporting_data) : item.supporting_data || {};
    if (sd.week1 !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1].filter(v => v !== undefined);
    if (sd.week1_current !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1_current].filter(v => v !== undefined);
    if (sd.night_week1 !== undefined) return [sd.night_week5, sd.night_week4, sd.night_week3, sd.night_week2, sd.night_week1].filter(v => v !== undefined);
    return [];
}

// ---------------------------------------------------------------------------
// Action Card (simplified — no acknowledge/dismiss buttons)
// ---------------------------------------------------------------------------
const ActionCard = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const trigger = TRIGGER_CONFIG[item.trigger_type] || {};
    const metrics = getMetrics(item);
    const weeklyData = getWeeklyData(item);
    const sevColor = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.medium;
    const sevLabel = item.severity ? item.severity.charAt(0).toUpperCase() + item.severity.slice(1) : 'Medium';

    return (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-2)', overflow: 'hidden', background: 'var(--color-bg-surface)' }}>
            <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '10px 14px', cursor: 'pointer' }}>
                {/* Severity dot */}
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor, flexShrink: 0 }} />

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{trigger.icon} {trigger.label}</span>
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: sevColor, textTransform: 'uppercase' }}>{sevLabel}</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.description || ''}
                    </div>
                </div>

                {/* Sparkline */}
                {weeklyData.length > 0 ? <WeeklySparkline data={weeklyData} color={trigger.color || '#2C7BE5'} /> : null}

                {/* Expand arrow */}
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
            </div>

            {/* Expanded details */}
            {expanded ? (
                <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
                        {metrics.map((m, i) => <MetricPill key={i} label={m.label} value={m.value} />)}
                    </div>
                    {item.description ? (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-3)', lineHeight: 1.5 }}>{item.description}</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const ResidentActionItems = ({ campusId, residentId }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
                method: 'POST',
                headers: await getHeaders(),
                body: JSON.stringify({
                    rep_type: 'residentactiontrends',
                    campus_id: campusId,
                    status: 'new',
                }),
            });
            const raw = await resp.json();
            const result = raw?.Status?.toLowerCase() === 'ok' ? raw.Res : raw?.Res || raw || {};
            const allItems = result?.action_items || [];
            // Filter to this resident only
            const filtered = allItems.filter(
                (item) => String(item.resident_id) === String(residentId)
            );
            setItems(filtered);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [campusId, residentId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Don't render anything if loading or no items for this resident
    if (loading || items.length === 0) return null;

    const highCount = items.filter((i) => i.severity === 'high').length;

    return (
        <div style={{ marginBottom: 'var(--space-3)' }}>
            {items.map((item) => (
                <ActionCard key={item.action_id} item={item} />
            ))}
        </div>
    );
};

export default ResidentActionItems;
